

















import { formatAddress } from '@/helpers';
import { IAddressDetails } from '@homeserve/od-typings/api';
import { ITraderAddress } from '@homeserve/od-typings/api';
import { Nullable } from '@homeserve/od-typings/utils';
import { addressHelper, IAddressSearchResult } from '@homeserve/vue-api-plugin';

import debounce from 'lodash/debounce';
import { FieldError, Validator } from 'vee-validate';
import { Component, Emit, Inject, Model, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class AddressField extends Vue {
  private static MIN_LENGTH = 3;

  @Inject('$validator')
  public readonly $validator!: Validator;

  @Model('change', { type: Object, default: () => null })
  public readonly address!: Nullable<ITraderAddress>;

  @Prop({ type: String, required: true })
  public readonly name!: string;

  @Prop({ type: String, default: null })
  public readonly formScope!: string;

  public addresses: IAddressSearchResult[] = [];
  public loading = false;
  public selectedAddress: Nullable<IAddressSearchResult> = null;

  public debounceFetchAddresses = debounce((postCode: string) => this.fetchAddresses(postCode), 200);

  public async fetchAddresses(input: string) {
    if (input.length < AddressField.MIN_LENGTH) {
      return;
    }

    this.loading = true;

    const { error, result } = await addressHelper.fetchAddressSearchResults(this.$store, {
      country: 'FR',
      input,
    });

    this.loading = false;

    if (error) {
      return;
    }

    this.addresses = Array.isArray(result) ? result : [];
  }

  public get items() {
    if (this.selectedAddress && !this.addresses.length) {
      return [this.selectedAddress];
    }

    return this.addresses;
  }

  public filterItems() {
    return true;
  }

  @Emit('change')
  public async updateAddress(addressSearchResult?: IAddressSearchResult) {
    return addressSearchResult ? await this.fetchAddress(addressSearchResult) : null;
  }

  @Watch('address', { immediate: true })
  private onAddressChanged(address: ITraderAddress) {
    this.selectedAddress = address ? this.convertToAddressSearchResult(address) : null;
  }

  private convertToTraderAddress(addressDetails: IAddressDetails): ITraderAddress {
    const { additionalAddress, country, postalBox, postCode, street, streetNo, town } = addressDetails;

    return {
      additionalAddress: additionalAddress || undefined,
      country,
      postalBox: postalBox || undefined,
      postCode,
      street,
      streetNo: streetNo || undefined,
      town,
    };
  }

  private convertToAddressSearchResult(address: ITraderAddress): IAddressSearchResult {
    const summary = formatAddress(address);

    const defaultAddress = {
      id: '',
      summary,
    };

    return this.addresses.find(item => item.summary === formatAddress(address)) || defaultAddress;
  }

  private async fetchAddress(addressSearchResult: IAddressSearchResult) {
    this.errors.remove(this.name, this.formScope);
    this.loading = true;

    const { error, result } = await addressHelper.fetchAddressDetails(this.$store, addressSearchResult.id);

    this.loading = false;

    if (error || typeof result === 'undefined') {
      const fieldError: FieldError = {
        field: this.name,
        msg: this.$t('onboarding.errors.fetchAddressDetails').toString(),
      };

      if (this.formScope) {
        fieldError.scope = this.formScope;
      }

      this.errors.add(fieldError);

      return null;
    }

    return this.convertToTraderAddress(result);
  }
}
